import { useIntl } from 'react-intl';

import { ConditionalRendering } from '@components/common/conditional-rendering';
import { EMPTY_VALUE } from '@constants/common.constants';
import { configs } from '@constants/configs/configs';
import { IAutoVerificationResult } from '../../constants/ticketlist.interface';
import {
    StyledAvAttachment,
    StyledAvCard,
    StyledAvCardCommentText,
    StyledAvCardLabel,
    StyledAvCardText,
    StyledAvCardTop,
    StyledAvComment,
    StyledAvDivider,
    StyledAvMetaRow,
    StyledAvMetaValue,
    StyledAvPanelTitle,
} from '../../constants/ticketlist.styled';
import { formatAvCommentLines, isRejectResult } from '../../constants/ticketlist.utils';
import { AvDecisionBadge } from './AvDecisionBadge';

interface IAvResultCardProps {
    data: IAutoVerificationResult;
}

const AvMultilineComment = ({ text, 'data-testid': testId }: { text?: string; 'data-testid'?: string }) => (
    <StyledAvCardCommentText data-testid={testId}>
        {formatAvCommentLines(text).map((line, index) => (
            <span key={index}>
                {index > 0 && <br />}
                {line}
            </span>
        ))}
    </StyledAvCardCommentText>
);

export const AvResultCard = ({ data }: IAvResultCardProps) => {
    const intl = useIntl();
    const reject = isRejectResult(data?.result);
    const attachments = data?.attachments || [];
    return (
        <StyledAvCard data-testid="av-result-card">
            <StyledAvCardTop>
                <StyledAvPanelTitle>
                    {intl.formatMessage({ id: 'ticket.autoVerification.result.cardTitle' })}
                </StyledAvPanelTitle>
                <ConditionalRendering isTrue={!!data?.result?.trim()}>
                    <AvDecisionBadge result={data.result} data-testid="av-card-badge" />
                </ConditionalRendering>

                <StyledAvMetaRow>
                    {intl.formatMessage({ id: 'ticket.autoVerification.banner.confidence' })}:&nbsp;
                    <StyledAvMetaValue data-testid="av-card-confidence">
                        {data?.confidence != null ? `${data.confidence}%` : EMPTY_VALUE}
                    </StyledAvMetaValue>
                    &nbsp;&nbsp;&nbsp;
                    {intl.formatMessage({ id: 'ticket.autoVerification.banner.mode' })}:&nbsp;
                    <StyledAvMetaValue data-testid="av-card-mode">{data?.mode || EMPTY_VALUE}</StyledAvMetaValue>
                </StyledAvMetaRow>
                <StyledAvMetaRow>
                    {intl.formatMessage({ id: 'ticket.autoVerification.banner.processedOn' })}:&nbsp;
                    <StyledAvMetaValue data-testid="av-card-processed-on">
                        {data?.processedOn || EMPTY_VALUE}
                    </StyledAvMetaValue>
                </StyledAvMetaRow>

                <ConditionalRendering isTrue={reject && !!data?.rejectReason}>
                    <div>
                        <StyledAvCardLabel>
                            {intl.formatMessage({ id: 'ticket.autoVerification.card.reasonForReject' })}
                        </StyledAvCardLabel>
                        <StyledAvCardText data-testid="av-card-reason">{data?.rejectReason || EMPTY_VALUE}</StyledAvCardText>
                    </div>
                </ConditionalRendering>

                <ConditionalRendering isTrue={!!attachments.length}>
                    <div>
                        <StyledAvCardLabel>
                            {intl.formatMessage({ id: 'ticket.autoVerification.card.attachments' })}
                        </StyledAvCardLabel>
                        {attachments.map((att, index) => (
                            <StyledAvAttachment
                                key={`${att.docPath || att.docId || 'att'}-${index}`}
                                href={configs.endpoints.TICKETS_LIST_VIEW_ATTACHMENT_URL.processReplacements({
                                    documentId: att.docPath,
                                })}
                                target="_blank"
                                rel="noreferrer"
                                data-testid={`av-card-attachment-${att.docPath || att.docId || index}`}
                            >
                                {att.docName}
                            </StyledAvAttachment>
                        ))}
                    </div>
                </ConditionalRendering>
            </StyledAvCardTop>

            <StyledAvComment>
                <StyledAvDivider />
                <StyledAvCardLabel>
                    {intl.formatMessage({ id: 'ticket.autoVerification.card.internalCommentSystem' })}
                </StyledAvCardLabel>
                <AvMultilineComment text={data?.internalComment} data-testid="av-card-internal-comment" />
            </StyledAvComment>

            <StyledAvComment>
                <StyledAvDivider />
                <StyledAvCardLabel>
                    {intl.formatMessage({ id: 'ticket.autoVerification.card.externalComment' })}
                </StyledAvCardLabel>
                <AvMultilineComment text={data?.externalComment} data-testid="av-card-external-comment" />
            </StyledAvComment>
        </StyledAvCard>
    );
};
